/**
 * AbrirTarpFlow (GUPM-178 Fase B) — container do rascunho "Em abertura".
 * Liga o `useTarpDraft` (autosave debounced + confirmar/descartar) ao
 * `AbrirTarpModal` presentacional. Deve ser montado com `key` por abertura
 * (novo acionamento ou retomada) para o hook reiniciar o estado do rascunho.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { QuickAction } from '@/components/tarp';
import type { Outcome } from '@/domain/tarp/flow';
import type { DamOption, TarpRecord } from '@/services/datasources';
import { useTarpDraft, useTarpMutations } from '../hooks';
import { AbrirTarpModal, type AbrirTarpPreselect } from './AbrirTarpModal';

function errMsg(e: unknown): string | null {
  if (!e) return null;
  return e instanceof Error ? e.message : String(e);
}

export function AbrirTarpFlow({
  action,
  dams,
  existingDraft,
  preselect = null,
  canDiscard,
  onClose,
}: {
  action: QuickAction;
  dams: DamOption[];
  /** Rascunho sendo retomado (null = novo acionamento). */
  existingDraft: TarpRecord | null;
  /** GUPM-185: pré-seleção vinda de notificação (barragem+instrumento). */
  preselect?: AbrirTarpPreselect | null;
  /** Se o usuário atual pode descartar (autor, RT ou admin). A RLS/trigger é o gate real. */
  canDiscard: boolean;
  onClose: () => void;
}) {
  // GUPM-293: o hook recebe o REGISTRO (não só o id) para conhecer o conteúdo já
  // gravado e não regravá-lo quando o formulário apenas se hidrata.
  const draft = useTarpDraft(existingDraft);
  const { conclude } = useTarpMutations();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abrir = confirmar a abertura (→ ABERTA) e encaminhar ao RT (→ CONCLUIDA_AGUARDANDO),
  // fiel ao fluxo atual do protótipo. O flush interno garante o autosave pendente.
  // GUPM-207: desfecho de emergência (`paebm`) encaminha ao módulo PAEBM após registrar.
  async function handleConfirm(outcome: Outcome) {
    setSubmitting(true);
    setError(null);
    try {
      const id = await draft.confirm();
      if (!id) {
        // O rascunho ainda não foi persistido (ex.: autosave falhou). NÃO fecha
        // em silêncio — a TARP não foi aberta.
        setError('Não foi possível abrir: o rascunho ainda não foi salvo. Verifique a conexão e tente de novo.');
        return;
      }
      await conclude.mutateAsync(id);
      onClose();
      // GUPM-207: acionamento de emergência registrado → vai ao PAEBM (guia de bolso).
      // Router-state efêmero (nunca estado em URL — ADR-0019 / regra de privacidade).
      if (outcome === 'paebm') navigate('/paebm', { state: { fromTarpId: id } });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDiscard() {
    setError(null);
    try {
      await draft.discard();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  // GUPM-262: "Manter rascunho" — grava o autosave pendente ANTES de fechar.
  // Se o flush falha, fechar descartaria a alteração em silêncio: fica aberto
  // com o erro visível.
  async function handleKeep() {
    setError(null);
    try {
      await draft.flush();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <AbrirTarpModal
      action={action}
      dams={dams}
      draft={existingDraft}
      preselect={preselect}
      onConfirm={(_input, outcome) => void handleConfirm(outcome)}
      onCancel={onClose}
      onChange={(c) =>
        draft.scheduleSave(
          // GUPM-185: no modo notificação o instrumento vem confirmado pela leitura
          // e é persistido no rascunho; no manual permanece null (identificado no protocolo).
          { damId: c.damId, instrumentId: preselect?.instrumentId ?? null, tarpCode: action.code },
          { flowState: c.flowState, observation: c.observation, stepsCompleted: c.stepsCompleted },
        )
      }
      onDiscard={() => void handleDiscard()}
      onKeep={() => void handleKeep()}
      // GUPM-293: o que decide se sair pede cerimônia é haver ALTERAÇÃO PENDENTE,
      // não existir rascunho. Um rascunho persistido e intocado já está salvo —
      // "Manter rascunho" prometia uma ação que não acontece, e a confirmação pedia
      // decisão sobre algo que não estava em risco. (Antes: `draftId !== null || dirty`.)
      // GUPM-298: a marca da VISITA, não o `dirty` do autosave — que dura 600 ms e
      // fazia o rótulo do rodapé trocar debaixo do cursor do usuário.
      hasPendingChanges={draft.alterouNestaVisita}
      // Descartar exige o usuário poder (autor, RT ou admin) e algo a descartar
      // (persistido ou pendente — descartar pendente = só cancelar o timer).
      // A RLS/trigger é o gate real.
      canDiscard={canDiscard && (draft.draftId !== null || draft.alterouNestaVisita)}
      saving={draft.saving}
      saveError={draft.saveError}
      submitting={submitting}
      error={error}
    />
  );
}
